import {
  CONTENT_HASH_ALGORITHM,
  CONTENT_HASH_HEX_PATTERN,
} from "../constants/content-hash";
import { encodeBytesBase64 } from "./base64";
import type { IComputeBlobContentHashProps } from "./content-hash.types";

// NOTE: NSAI-5143 digesting a blob materializes all of its bytes at once (Web
// Crypto has no streaming digest), so callers that fan out (a batch drop on the
// Uploads page starts one `uploadFile` per file, all concurrently) would hold N
// files' worth of ArrayBuffer at the same time and can exhaust the tab's memory
// before a single byte is uploaded. Every digest goes through this single-slot
// chain instead, so peak cost stays one blob no matter how many files (or
// callers) are in flight. Only hashing is serialized; the uploads themselves
// still run concurrently.
let hashQueue: Promise<unknown> = Promise.resolve();

// NOTE: NSAI-5229 exported so the mobile digest shares this one slot rather
// than opening a second, unbounded one. A phone picking several recordings at
// once is the same fan-out with far less memory to absorb it, and two separate
// queues would defeat the ceiling each of them enforces.
export const enqueueContentHash = <TResult>(
  task: () => Promise<TResult>,
): Promise<TResult> => {
  const result = hashQueue.then(task, task);
  // NOTE: Swallow the failure on the chain itself so one rejected digest does
  // not poison every hash queued behind it. `result` still rejects for its own
  // caller.
  hashQueue = result.then(
    () => undefined,
    () => undefined,
  );

  return result;
};

// NOTE: NSAI-5229 the digest → string step, shared so every platform spells the
// hash the same way. The server stores lowercase sha256 hex
// (`CONTENT_HASH_DIGEST_ENCODING` in server/src/vstr-audio/
// vstr-audio.constant.ts), and a hash that differs by so much as its casing
// compares equal to nothing.
// NOTE: NSAI-5229 accepts either shape a platform's digest returns: web's
// `crypto.subtle.digest` gives an `ArrayBuffer`, while mobile's streaming
// `@noble/hashes` digest gives a `Uint8Array`. `new Uint8Array(...)` wraps the
// first as a view and copies the second, both yielding the same 32 bytes.
export const toContentHashHex = (digest: ArrayBuffer | Uint8Array): string =>
  Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");

// NOTE: NSAI-6953 — turns a hex digest (what `computeBlobContentHash` below
// and mobile's `computeFileContentHash` return) into the checksum a presigned
// storage PUT and the skill resource API take: standard padded base64 of the
// same 32 bytes, 44 characters (`SKILL_BASE64_SHA256_PATTERN`). Converting
// the hex lets both clients keep their own digest code, queue and size
// ceilings. Returns null for anything but 64 lowercase hex characters, so the
// caller declares nothing instead of a value the API refuses with a 400.
export const toChecksumSha256Base64 = (
  contentHashHex: string,
): string | null => {
  if (!CONTENT_HASH_HEX_PATTERN.test(contentHashHex)) {
    return null;
  }

  // NOTE: Two hex characters per byte, the reverse of `toContentHashHex`. The
  // pattern check above guarantees 32 valid pairs.
  const digestBytes: number[] = [];
  for (let offset = 0; offset < contentHashHex.length; offset += 2) {
    digestBytes.push(
      Number.parseInt(contentHashHex.slice(offset, offset + 2), 16),
    );
  }

  return encodeBytesBase64(digestBytes);
};

// NOTE: SHA-256 hex of a blob's raw bytes — the same identity the backend
// derives server-side for duplicate detection, so a hash computed here can be
// compared against a stored `content_hash` directly.
//
// Returns `null` whenever the hash cannot (or should not) be computed:
// `crypto.subtle` is exposed only in secure contexts and on-premise installs
// can be served over plain HTTP; React Native's Hermes runtime has no
// `crypto.subtle` at all; and blobs past `maxSizeBytes` are skipped because Web
// Crypto has no streaming digest, so hashing has to materialize the whole file
// in an ArrayBuffer.
//
// NOTE: React Native reaches the same answer through `computeFileContentHash`
// in mobile/src/utils — it streams a file URI through an incremental
// `@noble/hashes` SHA-256 (Hermes has no `crypto.subtle`) instead of digesting a
// Blob with Web Crypto, and shares the queue and the hex encoding above
// (NSAI-5229).
//
// NOTE: `null` means "could not tell", never "no duplicate". Every caller has to
// stay correct without a hash — this is an early answer to a question the server
// still answers authoritatively against the real bytes.
export const computeBlobContentHash = async ({
  blob,
  maxSizeBytes,
}: IComputeBlobContentHashProps): Promise<string | null> => {
  const subtleCrypto = globalThis.crypto?.subtle;

  if (!subtleCrypto || blob.size > maxSizeBytes) {
    return null;
  }

  try {
    // NOTE: A queued task has no cancellation hook — once enqueued it runs to
    // completion even if its upload was aborted meanwhile, and the caller
    // discards the result via its own post-hash abort check. So a cancelled
    // large file still occupies the slot ahead of whatever is queued behind it.
    // Deliberate: the alternative is threading an AbortSignal through every
    // caller to save work only in the cancel-mid-batch case, and the memory
    // ceiling this queue exists to enforce holds either way.
    const digest = await enqueueContentHash(async () =>
      subtleCrypto.digest(CONTENT_HASH_ALGORITHM, await blob.arrayBuffer()),
    );

    return toContentHashHex(digest);
  } catch {
    // NOTE: Hashing is an optimization, never a hard requirement — a read
    // failure must not block the flow that asked for it.
    return null;
  }
};
